import React, { useState, useEffect } from 'react';
import { Play, Square, RefreshCw, AlertCircle } from 'lucide-react';
import { botAPI } from '../services/api';

function BotControl() {
  const [botStatus, setBotStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bidLimit, setBidLimit] = useState(75);

  useEffect(() => {
    fetchBotStatus();
    const interval = setInterval(fetchBotStatus, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchBotStatus = async () => {
    try {
      const response = await botAPI.getStatus();
      setBotStatus(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch bot status');
      console.error('Error fetching bot status:', err);
    }
  };

  const startBot = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await botAPI.start(bidLimit);
      setBotStatus(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start bot');
    } finally {
      setIsLoading(false);
    }
  };

  const stopBot = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await botAPI.stop();
      setBotStatus(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to stop bot');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-success-300 bg-success-900/40 border border-success-800/50';
      case 'stopped':
        return 'text-slate-400 bg-slate-800 border border-slate-600';
      case 'error':
        return 'text-danger-300 bg-danger-900/40 border border-danger-800/50';
      default:
        return 'text-slate-400 bg-slate-800 border border-slate-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <div className="w-2 h-2 bg-success-600 rounded-full animate-pulse" />;
      case 'stopped':
        return <div className="w-2 h-2 bg-slate-500 rounded-full" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <div className="w-2 h-2 bg-slate-500 rounded-full" />;
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-100">Bot Control</h2>
        <div className="flex items-center space-x-2">
          {getStatusIcon(botStatus?.is_running ? 'running' : 'stopped')}
          <span className={`status-badge ${getStatusColor(botStatus?.is_running ? 'running' : 'stopped')}`}>
            {botStatus?.is_running ? 'Running' : 'Stopped'}
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-danger-950/40 border border-danger-800/50 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-danger-400 mr-2" />
            <span className="text-danger-300">{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="label">Bid Limit</label>
          <input
            type="number"
            value={bidLimit}
            onChange={(e) => setBidLimit(parseInt(e.target.value) || 0)}
            className="input"
            min="1"
            max="1000"
            disabled={botStatus?.is_running}
          />
        </div>

        <div className="flex space-x-3">
          <button
            onClick={startBot}
            disabled={isLoading || botStatus?.is_running}
            className="btn btn-success flex items-center space-x-2"
          >
            <Play className="w-4 h-4" />
            <span>{isLoading ? 'Starting...' : 'Start Bot'}</span>
          </button>

          <button
            onClick={stopBot}
            disabled={isLoading || !botStatus?.is_running}
            className="btn btn-danger flex items-center space-x-2"
          >
            <Square className="w-4 h-4" />
            <span>{isLoading ? 'Stopping...' : 'Stop Bot'}</span>
          </button>

          <button
            onClick={fetchBotStatus}
            disabled={isLoading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {botStatus && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700">
            <div>
              <div className="text-sm text-slate-500">Bids Placed</div>
              <div className="text-2xl font-bold text-slate-100">
                {botStatus.bid_counter || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Processed Projects</div>
              <div className="text-2xl font-bold text-slate-100">
                {botStatus.processed_projects || 0}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BotControl;


